import { cn } from "@core/utils/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ComponentProps } from "react";
import type { IconType } from "react-icons";

// eslint-disable-next-line react-refresh/only-export-components
export const tabStyles = cva(
  [
    "relative",
    "cursor-pointer",
    "flex",
    "items-center",
    "justify-center",
    "gap-2",
    "py-1",
    "px-3",
    "whitespace-nowrap",
    "overflow-hidden",
    "transition-all",
    "duration-200",
    "ease-in",
    "disabled:text-neutral-400",
    "disabled:cursor-not-allowed",
  ],
  {
    variants: {
      variant: {
        link: "text-neutral-500 hover:text-neutral-700",
        button:
          "text-neutral-500 bg-neutral-100 rounded-lg hover:text-neutral-700",
      },
      selected: {
        true: "", // This is a placeholder that will be overridden by compoundVariants
        false: "",
      },
      size: {
        lg: "h-[3.1875rem] py-1 px-3",
        md: "h-[2.5625rem] py-1 px-3",
        sm: "h-[2rem] py-1 px-3",
      },
    },
    compoundVariants: [
      {
        variant: "link",
        selected: true,
        className: "border-b-2 font-medium border-primary-800 text-neutral-950",
      },
      {
        variant: "button",
        selected: true,
        className: "border-b-2 border-primary-800 text-neutral-950 font-medium",
      },
    ],
    defaultVariants: {
      variant: "link",
      size: "md",
      selected: false,
    },
  },
);

type ButtonProps = ComponentProps<"button"> &
  VariantProps<typeof tabStyles> & {
    leftIcon?: IconType | string;
    rightIcon?: IconType | string;
    selected?: boolean;
    iconStrokeWidth?: number;
  };

const Tab = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant,
      className,
      size,
      children,
      selected = false,
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      iconStrokeWidth = 0.7,
      disabled: _disabled,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        role="tab"
        aria-selected={selected ?? false}
        className={cn(
          tabStyles({
            variant,
            size,
            selected, // Pass the selected prop here
          }),
          "flex items-center gap-2",
          className,
        )}
        {...props}
      >
        {LeftIcon && (
          <LeftIcon
            className="flex-shrink-0 text-2xl"
            strokeWidth={iconStrokeWidth}
          />
        )}

        {children && <div className="flex items-center gap-3">{children}</div>}

        {RightIcon && (
          <RightIcon
            className="flex-shrink-0 text-2xl"
            strokeWidth={iconStrokeWidth}
          />
        )}
      </button>
    );
  },
);

Tab.displayName = "Tab";

export default Tab;
